import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="mb-8">
    <h2 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h2>
    {children}
  </section>
);

const OverviewPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-pink-200 to-blue-300 p-6 md:p-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-extrabold text-indigo-900 mb-10 text-center">
          My Code Dragon: Empowering Young Coders
        </h1>

        <Section title="Engaging Learning Experience">
          <p className="text-gray-700 leading-relaxed mb-4">
            Learn coding at your own pace with hands-on activities and real-time
            feedback from My Code Dragon AI, on an easy-to-navigate platform.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Whether you're a beginner or advanced, My Code Dragon offers courses
            that match your skill level, helping you progress step by step.
          </p>
        </Section>

        <Section title="Course Offerings">
          <p className="text-gray-700 leading-relaxed mb-4">
            We offer courses ranging from beginner to advanced levels in the
            following languages:
          </p>
          <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-4">
            <li>Python</li>
            <li>R</li>
            <li>SQL</li>
            <li>JavaScript</li>
            <li>HTML & CSS</li>
          </ul>
          <p className="text-gray-700 leading-relaxed">
            Each course is designed to provide a solid foundation in coding,
            with coding activities that build toward real-world applications.
            Whether you're starting with the basics or enhancing your existing
            skills, My Code Dragon has a course for you. Check out our{' '}
            <Link className="text-blue-700 font-bold" to={'/courses'}>
              courses!
            </Link>
          </p>
        </Section>

        <Section title="Real-Time Assistance from AI">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-indigo-900">
              For Students
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Stuck on a problem? My Code Dragon AI offers personalized guidance
              and debugging tips instantly.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-indigo-900">For Parents</h3>
            <p className="text-gray-700 leading-relaxed">
              AI-driven assistance ensures your child receives effective help,
              promoting independence and skill development.
            </p>
          </div>
        </Section>

        <Section title="Progress Tracking and Achievements">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-indigo-900">
              For Students
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Track your progress, set goals, and earn Dragon Mastery Medals as
              you complete courses and challenges.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-indigo-900">For Parents</h3>
            <p className="text-gray-700 leading-relaxed">
              Stay updated with detailed reports that track coding hours,
              completed courses, and achievements.
            </p>
          </div>
        </Section>

        <Section title="Flexible Learning Schedule">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-indigo-900">
              For Students
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Set your own pace and code whenever it works best for you.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-indigo-900">For Parents</h3>
            <p className="text-gray-700 leading-relaxed">
              My Code Dragon offers flexible scheduling to fit your child's busy
              school life.
            </p>
          </div>
        </Section>

        <Section title="Safe and Parent-Friendly Environment">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-indigo-900">
              For Students
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Code in a secure environment designed for students—focused and
              distraction-free.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-indigo-900">For Parents</h3>
            <p className="text-gray-700 leading-relaxed">
              My Code Dragon ensures a safe online space, with progress
              monitoring and support for your child’s success.
            </p>
          </div>
        </Section>

        <Section title="Real-World Coding Projects">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-indigo-900">
              For Students
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Work on real-world coding challenges that build practical
              experience.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-indigo-900">For Parents</h3>
            <p className="text-gray-700 leading-relaxed">
              Your child will apply coding skills to real projects, giving them
              a sense of accomplishment.
            </p>
          </div>
        </Section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-3xl font-semibold text-white mb-4">
              Start Your Coding Journey Today!
            </h2>
            <p className="text-white text-lg mb-6">
              Join My Code Dragon and unlock your potential in coding.
            </p>
            <Link to={'/signup'}>
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-6 rounded-lg shadow-lg inline-flex items-center">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OverviewPage;
